import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { SectionHeader } from "@/components/common/Widgets";
import ContextualUploader from "@/components/common/ContextualUploader";
import { Package2, TrendingUp, TrendingDown, AlertTriangle, CheckCircle2, Plus, History, BarChart3 } from "lucide-react";
import { fmtDate } from "@/lib/format";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip as RTooltip, ReferenceLine, Bar, BarChart } from "recharts";

function Field({ label, children, hint }) {
  return (
    <div className="space-y-1">
      <Label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{label}</Label>
      {children}
      {hint && <p className="text-[10px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

const fmtInr = (n) => new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(n || 0);

function emptyForm() {
  return {
    stmt_date: new Date().toISOString().slice(0, 10),
    period: "",
    stock_raw_material: 0,
    stock_work_in_progress: 0,
    stock_finished_goods: 0,
    stock_others: 0,
    stock_margin_pct: 25,
    book_debts_lt90: 0,
    book_debts_gt90: 0,
    debtors_margin_pct: 25,
    creditors_sundry: 0,
    creditors_advance: 0,
    physically_verified: false,
    verified_by_name: "",
    visit_date: "",
    photos_uploaded: 0,
    remarks: "",
  };
}

function DPSummary({ calc }) {
  if (!calc) return null;
  const breach = calc.breach;
  return (
    <Card className={`p-4 ${breach ? "border-destructive/40 bg-destructive/5" : "border-success/30 bg-success/5"}`} data-testid="stock-dp-summary">
      <div className="flex items-center gap-2 mb-2">
        {breach ? <AlertTriangle className="h-5 w-5 text-destructive" /> : <CheckCircle2 className="h-5 w-5 text-success" />}
        <h3 className="font-semibold heading text-sm">Drawing Power Summary</h3>
        {breach ? <Badge variant="outline" className="text-destructive border-destructive/40">BREACH</Badge> : <Badge variant="outline" className="text-success border-success/40">WITHIN LIMIT</Badge>}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Eligible Stock" value={`₹ ${fmtInr(calc.eligible_stock)}`} />
        <Stat label="Eligible Debtors" value={`₹ ${fmtInr(calc.eligible_debtors)}`} />
        <Stat label="Creditors" value={`₹ ${fmtInr(calc.creditors_total)}`} />
        <Stat label="Drawing Power" value={`₹ ${(calc.drawing_power_lakhs || 0).toFixed(2)} L`} accent />
        <Stat label="Sanctioned Limit" value={`₹ ${(calc.sanctioned_limit_lakhs || 0).toFixed(2)} L`} />
        <Stat label="DP / Sanction" value={calc.dp_vs_sanction_pct != null ? `${calc.dp_vs_sanction_pct.toFixed(1)}%` : "—"} />
        {breach && <Stat label="Shortfall" value={`₹ ${(calc.breach_amount_lakhs || 0).toFixed(2)} L`} danger />}
      </div>
    </Card>
  );
}

function Stat({ label, value, accent, danger }) {
  return (
    <div className="rounded-md bg-card border border-border p-2">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`text-base font-semibold mt-0.5 ${accent ? "text-accent" : danger ? "text-destructive" : ""}`}>{value}</div>
    </div>
  );
}

function DPTrendCharts({ list }) {
  // list is desc by stmt_date — reverse for chronological chart
  const points = [...list].reverse().map((r) => ({
    date: r.stmt_date,
    DP: r.calc?.drawing_power_lakhs || 0,
    Sanctioned: r.calc?.sanctioned_limit_lakhs || 0,
    Stock: ((r.calc?.stock_total || 0) / 1_00_000).toFixed(2) * 1,
    Debtors: ((r.calc?.eligible_debtors || 0) / 1_00_000).toFixed(2) * 1,
    Creditors: ((r.calc?.creditors_total || 0) / 1_00_000).toFixed(2) * 1,
  }));
  // Variance = month-over-month change in DP
  const variance = points.map((p, i) => ({
    date: p.date,
    deltaDP: i === 0 ? 0 : +(p.DP - points[i - 1].DP).toFixed(2),
    DP: p.DP,
  }));
  const latestDp = points[points.length - 1]?.DP || 0;
  const prevDp = points[points.length - 2]?.DP || 0;
  const monthDelta = latestDp - prevDp;
  const monthPct = prevDp ? (monthDelta / prevDp) * 100 : 0;

  return (
    <Card className="p-5" data-testid="stock-trend-chart">
      <div className="flex items-center gap-2 mb-3">
        <BarChart3 className="h-4 w-4 text-accent" />
        <h3 className="font-semibold heading text-sm">DP Trend & Variance ({points.length} statements)</h3>
        <span className="text-xs ml-auto flex items-center gap-1">
          {monthDelta >= 0 ? <TrendingUp className="h-3 w-3 text-success" /> : <TrendingDown className="h-3 w-3 text-destructive" />}
          <span className={`mono ${monthDelta >= 0 ? "text-success" : "text-destructive"}`}>
            {monthDelta >= 0 ? "+" : ""}{monthDelta.toFixed(2)} L ({monthPct.toFixed(1)}%) vs prev
          </span>
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="space-y-1">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Drawing Power vs Sanctioned Limit (₹L)</div>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={points} margin={{ top: 8, right: 6, left: -10, bottom: 0 }}>
              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <RTooltip wrapperStyle={{ fontSize: 11 }} />
              <ReferenceLine y={points[0]?.Sanctioned || 0} stroke="hsl(var(--warning))" strokeDasharray="4 4" label={{ value: "Sanctioned", fontSize: 9, fill: "hsl(var(--warning))", position: "insideTopRight" }} />
              <Line type="monotone" dataKey="DP" stroke="hsl(var(--accent))" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="space-y-1">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Components: Stock / Debtors / Creditors (₹L)</div>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={points} margin={{ top: 8, right: 6, left: -10, bottom: 0 }}>
              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <RTooltip wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="Stock" fill="hsl(var(--accent))" />
              <Bar dataKey="Debtors" fill="hsl(var(--primary))" />
              <Bar dataKey="Creditors" fill="hsl(var(--destructive))" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Variance table */}
      <div className="mt-4 pt-4 border-t border-border">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-2">Month-over-Month DP Variance</div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase text-muted-foreground">
              <tr><th className="text-left pb-1">Date</th><th className="text-right pb-1">DP (₹L)</th><th className="text-right pb-1">Δ vs prev (₹L)</th><th className="text-right pb-1">Δ %</th></tr>
            </thead>
            <tbody>
              {variance.map((v, i) => {
                const prev = points[i - 1]?.DP;
                const pct = prev ? (v.deltaDP / prev) * 100 : 0;
                const cls = i === 0 ? "text-muted-foreground" : v.deltaDP >= 0 ? "text-success" : "text-destructive";
                return (
                  <tr key={v.date} className="border-t border-border/60">
                    <td className="py-1 mono">{v.date}</td>
                    <td className="py-1 text-right mono">{v.DP.toFixed(2)}</td>
                    <td className={`py-1 text-right mono ${cls}`}>{i === 0 ? "—" : `${v.deltaDP >= 0 ? "+" : ""}${v.deltaDP.toFixed(2)}`}</td>
                    <td className={`py-1 text-right mono ${cls}`}>{i === 0 ? "—" : `${pct >= 0 ? "+" : ""}${pct.toFixed(1)}%`}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </Card>
  );
}

export default function StockStatementSection({ app, reload }) {
  const [list, setList] = useState([]);
  const [latest, setLatest] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [busy, setBusy] = useState(false);

  const loanType = (app?.loan_type || "").toUpperCase();
  const applies = ["CC", "SME_CC", "WC_NAYAK", "OD"].includes(loanType);

  const load = async () => {
    try {
      const [l, lt] = await Promise.all([
        api.get(`/stock-statements/${app.id}`),
        api.get(`/stock-statements/${app.id}/latest`),
      ]);
      setList(l.data); setLatest(lt.data?.latest || null);
    } catch (e) {}
  };
  useEffect(() => { if (applies) load(); /* eslint-disable-next-line */ }, [app?.id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.stmt_date) return toast.error("Statement date required");
    setBusy(true);
    try {
      const payload = { application_id: app.id, ...form };
      // Coerce numerics
      ["stock_raw_material","stock_work_in_progress","stock_finished_goods","stock_others",
       "stock_margin_pct","book_debts_lt90","book_debts_gt90","debtors_margin_pct",
       "creditors_sundry","creditors_advance","photos_uploaded"].forEach((k) => { payload[k] = Number(payload[k]) || 0; });
      const r = await api.post("/stock-statements", payload);
      toast.success("Stock statement submitted");
      setShowForm(false); setForm(emptyForm());
      load();
      if (r.data?.calc?.breach) toast.warning(`Drawing Power ₹${r.data.calc.drawing_power_lakhs}L is BELOW Sanctioned Limit ₹${r.data.calc.sanctioned_limit_lakhs}L`, { duration: 8000 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit");
    }
    setBusy(false);
  };

  if (!applies) {
    return (
      <Card className="p-8 text-center border-dashed">
        <Package2 className="h-10 w-10 text-muted-foreground/40 mx-auto mb-2" />
        <p className="text-sm text-muted-foreground">Stock statements only apply to working-capital facilities (CC / OD / SME-CC / WC).</p>
        <p className="text-[11px] text-muted-foreground mt-1">Current loan type: {loanType || "—"}</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Stock Statement & Drawing Power"
        subtitle="Periodic stock + book-debt statement used to compute Drawing Power for the CC/OD facility."
        action={
          <Button onClick={() => setShowForm(!showForm)} data-testid="stock-add-button">
            <Plus className="h-4 w-4 mr-1" /> {showForm ? "Cancel" : "New Stock Statement"}
          </Button>
        }
      />

      {latest && <DPSummary calc={latest.calc} />}

      {list.length >= 2 && <DPTrendCharts list={list} />}

      {showForm && (
        <Card className="p-5 space-y-4 border-accent/30 bg-accent/5" data-testid="stock-form">
          <h3 className="font-semibold heading text-sm">New Stock Statement · As of date</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Statement Date *"><Input type="date" value={form.stmt_date} onChange={(e) => set("stmt_date", e.target.value)} data-testid="stock-stmt-date" /></Field>
            <Field label="Period (e.g., May-2026)"><Input value={form.period} onChange={(e) => set("period", e.target.value)} /></Field>
            <Field label="Visit Date"><Input type="date" value={form.visit_date} onChange={(e) => set("visit_date", e.target.value)} /></Field>
          </div>

          <div className="pt-3 border-t">
            <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">Stock components (₹)</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Field label="Raw Material"><Input type="number" value={form.stock_raw_material} onChange={(e) => set("stock_raw_material", e.target.value)} /></Field>
              <Field label="Work-in-progress"><Input type="number" value={form.stock_work_in_progress} onChange={(e) => set("stock_work_in_progress", e.target.value)} /></Field>
              <Field label="Finished Goods"><Input type="number" value={form.stock_finished_goods} onChange={(e) => set("stock_finished_goods", e.target.value)} /></Field>
              <Field label="Others"><Input type="number" value={form.stock_others} onChange={(e) => set("stock_others", e.target.value)} /></Field>
              <Field label="Stock Margin %" hint="Typically 25%"><Input type="number" value={form.stock_margin_pct} onChange={(e) => set("stock_margin_pct", e.target.value)} /></Field>
            </div>
          </div>

          <div className="pt-3 border-t">
            <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">Book Debts (₹)</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Field label="Debtors &lt; 90 days" hint="Eligible"><Input type="number" value={form.book_debts_lt90} onChange={(e) => set("book_debts_lt90", e.target.value)} /></Field>
              <Field label="Debtors &gt; 90 days" hint="Ineligible — for info only"><Input type="number" value={form.book_debts_gt90} onChange={(e) => set("book_debts_gt90", e.target.value)} /></Field>
              <Field label="Debtors Margin %" hint="Typically 25%"><Input type="number" value={form.debtors_margin_pct} onChange={(e) => set("debtors_margin_pct", e.target.value)} /></Field>
            </div>
          </div>

          <div className="pt-3 border-t">
            <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">Creditors (₹) — deducted</div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Sundry Creditors"><Input type="number" value={form.creditors_sundry} onChange={(e) => set("creditors_sundry", e.target.value)} /></Field>
              <Field label="Advance from Customers"><Input type="number" value={form.creditors_advance} onChange={(e) => set("creditors_advance", e.target.value)} /></Field>
            </div>
          </div>

          <div className="pt-3 border-t">
            <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">Verification</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
              <Field label="Physically Verified"><div className="flex items-center gap-2 pt-1"><Switch checked={form.physically_verified} onCheckedChange={(v) => set("physically_verified", v)} id="phyver" data-testid="stock-phy-verified" /><Label htmlFor="phyver" className="text-xs">Confirmed at site</Label></div></Field>
              <Field label="Verified By"><Input value={form.verified_by_name} onChange={(e) => set("verified_by_name", e.target.value)} placeholder="Officer name" /></Field>
              <Field label="Photos uploaded"><Input type="number" value={form.photos_uploaded} onChange={(e) => set("photos_uploaded", e.target.value)} /></Field>
              <Field label="Remarks" hint="Any anomalies, market conditions, etc."><Textarea value={form.remarks} onChange={(e) => set("remarks", e.target.value)} rows={2} /></Field>
            </div>
          </div>

          <div className="pt-3 border-t">
            <ContextualUploader applicationId={app.id} category="STOCK_STATEMENT" title="Attach stock statement / photos" accept="image/*,application/pdf" multiple />
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="ghost" onClick={() => { setShowForm(false); setForm(emptyForm()); }}>Cancel</Button>
            <Button onClick={submit} disabled={busy} data-testid="stock-submit-button">{busy ? "Submitting…" : "Submit Stock Statement"}</Button>
          </div>
        </Card>
      )}

      {list.length > 0 && (
        <Card className="p-0 overflow-hidden">
          <div className="px-5 py-3 border-b flex items-center gap-2 bg-muted/30">
            <History className="h-4 w-4 text-muted-foreground" />
            <h3 className="font-semibold heading text-sm">Statement History ({list.length})</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="text-left px-3 py-2.5">Stmt Date</th>
                  <th className="text-left px-3 py-2.5">Period</th>
                  <th className="text-right px-3 py-2.5">Stock Total</th>
                  <th className="text-right px-3 py-2.5">Eligible Debtors</th>
                  <th className="text-right px-3 py-2.5">DP (₹L)</th>
                  <th className="text-right px-3 py-2.5">Sanctioned (₹L)</th>
                  <th className="text-center px-3 py-2.5">Status</th>
                  <th className="text-left px-3 py-2.5">Submitted By</th>
                </tr>
              </thead>
              <tbody>
                {list.map((r) => (
                  <tr key={r.id} className="border-t border-border">
                    <td className="px-3 py-2 mono text-xs">{r.stmt_date}</td>
                    <td className="px-3 py-2 text-xs">{r.period || "—"}</td>
                    <td className="px-3 py-2 text-right mono text-xs">₹ {fmtInr(r.calc?.stock_total)}</td>
                    <td className="px-3 py-2 text-right mono text-xs">₹ {fmtInr(r.calc?.eligible_debtors)}</td>
                    <td className="px-3 py-2 text-right mono text-xs font-medium">{(r.calc?.drawing_power_lakhs || 0).toFixed(2)}</td>
                    <td className="px-3 py-2 text-right mono text-xs">{(r.calc?.sanctioned_limit_lakhs || 0).toFixed(2)}</td>
                    <td className="px-3 py-2 text-center">
                      {r.calc?.breach
                        ? <Badge variant="outline" className="text-destructive border-destructive/40 text-[10px]"><TrendingDown className="h-2.5 w-2.5 mr-1 inline" />BREACH</Badge>
                        : <Badge variant="outline" className="text-success border-success/40 text-[10px]"><TrendingUp className="h-2.5 w-2.5 mr-1 inline" />OK</Badge>}
                    </td>
                    <td className="px-3 py-2 text-xs"><div>{r.submitted_by_name}</div><div className="text-muted-foreground text-[10px]">{fmtDate(r.submitted_at)}</div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
